import { createParser } from 'eventsource-parser';
import { useCallback } from 'react';

import {
  useAgentousPrivateRequest,
  useUnauthenticatedAgentRequest,
} from '@/lib/axios/use-private-request';
import { type ChatStreamEvent } from '@/types/agent-chat';
import {
  type ChatHistoryItem,
  type PaginatedChatHistoryResponse,
} from '@/types/agents';
import { agenticService } from '@/utils/api-service-controllers-route';
import { BASE_URL } from '@/utils/api-urls';
import { parseChatStreamEventData } from '@/utils/chat-stream-events';

import { useAuth } from '../context/auth-context';
import { useTenant } from '../context/tenant-context';
import { extractErrorMessage } from '../utils/error-utils';

// Utility function to filter out requestField JSON data from messages
export const filterRequestFieldFromMessage = (message: string): string => {
  if (!message) return message;

  let filteredMessage = message;

  // If the message contains {"requestField, cut off everything from there
  const requestFieldIndex = filteredMessage.indexOf('{"requestField');
  if (requestFieldIndex !== -1) {
    filteredMessage = filteredMessage.substring(0, requestFieldIndex);
  }

  // Remove any markdown JSON blocks (```json { ... } ```)
  filteredMessage = filteredMessage
    .replace(/```json\s*\{.*?\}\s*```/gs, '')
    .trim();

  // Remove plain JSON from display (legacy fallback)
  filteredMessage = filteredMessage
    .replace(/\{\s*"requestField"\s*:\s*"[^"]+"\s*\}\s*/g, '')
    .trim();

  // Clean up excessive whitespace and line breaks
  filteredMessage = filteredMessage.replace(/\s{3,}/g, '\n\n').trim();

  return filteredMessage;
};

export const CHAT_ENDPOINT = `${agenticService}/ai-agents/chat`;
export const HISTORY_ENDPOINT = `${CHAT_ENDPOINT}/history`;
export const PAGINATED_HISTORY_ENDPOINT = `${CHAT_ENDPOINT}/history/paginated`;

type ApiResponse<T> = Promise<T>;

const useAgentApi = () => {
  const { tenantId, activeAgent, isAgentSwitching, waitForAgentReady } =
    useTenant();
  const { isAuthenticated } = useAuth();
  const agentKey = activeAgent || '';

  // Always call both hooks to maintain consistent hook order
  const authenticatedRequest = useAgentousPrivateRequest(
    BASE_URL,
    tenantId || '',
    agentKey
  );
  const unauthenticatedRequest = useUnauthenticatedAgentRequest(
    BASE_URL,
    agentKey
  );

  // Return the appropriate request based on authentication status
  const axiosInstanceRef = isAuthenticated
    ? authenticatedRequest
    : unauthenticatedRequest;

  return {
    axiosInstanceRef,
    activeAgent,
    isAgentSwitching,
    waitForAgentReady,
  };
};

interface ScyraChatStreamOptions {
  userMessage: string;
  sessionId: string;
  agentKey?: string;
  signal?: AbortSignal;
  onEvent: (event: ChatStreamEvent) => void;
}

export const useAgentChatStreamApi = () => {
  const { axiosInstanceRef, activeAgent, isAgentSwitching, waitForAgentReady } =
    useAgentApi();

  const streamChat = useCallback(
    async (options: ScyraChatStreamOptions): ApiResponse<string> => {
      const { userMessage, sessionId, agentKey, signal, onEvent } = options;
      const expectedAgentKey = agentKey || activeAgent || '';

      try {
        if (isAgentSwitching) {
          const isReady = await waitForAgentReady(expectedAgentKey);
          if (!isReady) {
            throw new Error('Agent switch timeout - chat request cancelled');
          }
        }

        const isAxiosReady = await axiosInstanceRef.waitForReady(
          expectedAgentKey,
          3000
        );
        if (!isAxiosReady || !axiosInstanceRef.current) {
          throw new Error(
            'Chat service not ready. Please wait a moment and try again.'
          );
        }

        const axiosDefaults = axiosInstanceRef.current.defaults.headers || {};
        const extractHeaders = (source: unknown) => {
          if (!source || typeof source !== 'object') return {};
          return Object.entries(source as Record<string, unknown>).reduce(
            (acc, [key, value]) => {
              if (typeof value === 'string') {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, string>
          );
        };

        const mergedHeaders = {
          ...extractHeaders(axiosDefaults.common),
          ...extractHeaders(axiosDefaults.post),
          ...extractHeaders(axiosDefaults),
        };

        const response = await fetch(`${BASE_URL}${CHAT_ENDPOINT}`, {
          method: 'POST',
          headers: {
            ...mergedHeaders,
            ...(expectedAgentKey ? { 'X-Active-Agent': expectedAgentKey } : {}),
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userMessage, sessionId, stream: true }),
          cache: 'no-store',
          signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType && !contentType.includes('text/event-stream')) {
          throw new Error(
            `Expected chat stream response, received ${contentType}`
          );
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Chat stream response did not include a body.');
        }

        const decoder = new TextDecoder('utf-8');
        let accumulatedMessage = '';
        let finalMessage = '';

        const parser = createParser({
          onEvent(event) {
            const streamEvent = parseChatStreamEventData(
              event.data,
              event.event
            );
            if (!streamEvent) return;

            if (streamEvent.type === 'delta' && streamEvent.text) {
              accumulatedMessage += streamEvent.text;
            }

            if (streamEvent.type === 'final') {
              finalMessage = streamEvent.text ?? accumulatedMessage;
            }

            onEvent(streamEvent);
          },
        });

        try {
          while (true) {
            if (signal?.aborted) {
              throw new DOMException(
                'The operation was aborted.',
                'AbortError'
              );
            }

            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            parser.feed(chunk);
          }
        } finally {
          reader.releaseLock();
        }

        return filterRequestFieldFromMessage(
          finalMessage || accumulatedMessage
        );
      } catch (error) {
        const agentName =
          axiosInstanceRef.current?.defaults?.headers?.['X-Active-Agent'] ||
          activeAgent ||
          'agent';
        console.error(`${agentName} Chat Stream Error:`, error);
        const errorMessage = extractErrorMessage(error);
        throw new Error(errorMessage);
      }
    },
    [axiosInstanceRef, activeAgent, isAgentSwitching, waitForAgentReady]
  );

  return streamChat;
};

// Chat history hook - always uses authenticated requests
export const useAgentChatHistoryApi = () => {
  const { tenantId, activeAgent, isAgentSwitching, waitForAgentReady } =
    useTenant();
  const agentKey = activeAgent || '';

  // Always use authenticated request for chat history since it's user-specific data
  const authenticatedRequest = useAgentousPrivateRequest(
    BASE_URL,
    tenantId || '',
    agentKey
  );

  const fetchChatHistory = useCallback(async (): ApiResponse<
    ChatHistoryItem[]
  > => {
    try {
      // Wait for agent to be ready if switching
      if (isAgentSwitching) {
        const isReady = await waitForAgentReady();
        if (!isReady) {
          return [];
        }
      }

      // Wait for axios instance to be ready (without specifying agent)
      const isAxiosReady = await authenticatedRequest.waitForReady(
        undefined,
        3000
      );
      if (!isAxiosReady || !authenticatedRequest.current) {
        return [];
      }

      const axiosInstance = authenticatedRequest.current;

      // Ensure we have the Authorization header before making the request
      const authHeader = axiosInstance.defaults?.headers?.['Authorization'];
      if (!authHeader) {
        return [];
      }

      const { data } = await axiosInstance.get(HISTORY_ENDPOINT);

      // Normalize messages
      const normalizedHistory: ChatHistoryItem[] = (data || []).map(
        (item: ChatHistoryItem) => {
          return {
            ...item,
            message: filterRequestFieldFromMessage(item.message),
            createdAt: item.createdAt,
          };
        }
      );

      return normalizedHistory;
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      // Return empty array instead of throwing to prevent chat failures
      return [];
    }
  }, [authenticatedRequest, isAgentSwitching, waitForAgentReady]);

  return fetchChatHistory;
};

// Paginated chat history hook - always uses authenticated requests
export const useAgentChatHistoryPaginatedApi = () => {
  const { tenantId, activeAgent, isAgentSwitching, waitForAgentReady } =
    useTenant();
  const agentKey = activeAgent || '';

  // Always use authenticated request for chat history since it's user-specific data
  const authenticatedRequest = useAgentousPrivateRequest(
    BASE_URL,
    tenantId || '',
    agentKey
  );

  const fetchPaginatedChatHistory = useCallback(
    async (
      page: number = 1,
      pageSize: number = 20
    ): ApiResponse<PaginatedChatHistoryResponse> => {
      try {
        // Wait for agent to be ready if switching
        if (isAgentSwitching) {
          const isReady = await waitForAgentReady();
          if (!isReady) {
            console.warn(
              `Agent switch timeout for ${activeAgent} - returning empty paginated chat history`
            );
            return {
              status: false,
              message: 'Agent switch timeout.',
              error: 'AGENT_SWITCH_TIMEOUT',
              data: {
                items: [],
                total: 0,
                page,
                pageSize,
              },
            };
          }
        }

        // Wait for the axios instance to be ready (without specifying agent)
        // The React Query key includes agent, so stale queries are invalidated naturally
        // We don't pass an expected agent because the closure's agentKey may be stale
        const isAxiosReady = await authenticatedRequest.waitForReady(
          undefined, // Don't specify agent - just wait for instance to be ready
          3000
        );
        if (!isAxiosReady || !authenticatedRequest.current) {
          // If not ready, it might be due to agent switch - let React Query retry with new agent
          return {
            status: false,
            message: 'Service not ready, will retry.',
            error: 'AXIOS_NOT_READY',
            data: {
              items: [],
              total: 0,
              page,
              pageSize,
            },
          };
        }

        const axiosInstance = authenticatedRequest.current;

        // Ensure we have the Authorization header before making the request
        const authHeader = axiosInstance.defaults?.headers?.['Authorization'];
        if (!authHeader) {
          console.warn(
            'No Authorization header found, waiting for authentication...'
          );
          return {
            status: false,
            message: 'Waiting for authentication...',
            error: 'NO_AUTH_HEADER',
            data: {
              items: [],
              total: 0,
              page,
              pageSize,
            },
          };
        }

        const { data } = await axiosInstance.get(
          `${PAGINATED_HISTORY_ENDPOINT}?page=${page}&pageSize=${pageSize}`
        );

        // Normalize messages in the response
        const normalizedResponse: PaginatedChatHistoryResponse = {
          ...data,
          data: {
            ...data.data,
            items: (data.data?.items || []).map((item: ChatHistoryItem) => ({
              ...item,
              message: filterRequestFieldFromMessage(item.message),
              createdAt: item.createdAt,
            })),
          },
        };

        return normalizedResponse;
      } catch (error) {
        console.error(
          `Failed to fetch paginated chat history for agent ${activeAgent}, page ${page}:`,
          error
        );
        // Return error response with proper error flag
        return {
          status: false,
          message: 'Failed to fetch chat history.',
          error: 'FETCH_ERROR',
          data: {
            items: [],
            total: 0,
            page,
            pageSize,
          },
        };
      }
    },
    [authenticatedRequest, activeAgent, isAgentSwitching, waitForAgentReady]
  );

  return fetchPaginatedChatHistory;
};
